import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "../actions/auth";
import { FEATURE_ADDON_KEYS } from "../actions/featureAddons";
import useAgencyIsAppSumo from "./useAgencyIsAppSumo";
import { buildSeatsStatus } from "../utils/extraSeats";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import { ID_ADLE_THEME } from "../utils/constants";
import {
  AGENCY_ADDONS_VERSION_RTD_KEY,
  TIERED_ADDON_KEYS,
  featureAddonCatalogPath,
  normalizeAddonsVersion,
  buildFeatureAddonStatus,
} from "../utils/featureAddonCatalog";
import { buildWhiteLabelClientStatus } from "../utils/whiteLabelAddon";

const useAgencyAddonSource = ({ enabled }) => {
  const dispatch = useDispatchApp();
  const authUser = useSelectorApp((state) => state.auth.user);
  const { agencyID } = dispatch(getPathDatabaseByRole());
  const [agencyAddons, setAgencyAddons] = useState(null);
  const [addonsVersion, setAddonsVersion] = useState(null);
  const [catalogRoot, setCatalogRoot] = useState(null);
  const [addonsResolved, setAddonsResolved] = useState(false);
  const [catalogResolved, setCatalogResolved] = useState(false);

  useEffect(() => {
    if (!enabled || !authUser?.uid || !agencyID) {
      setAgencyAddons(null);
      setAddonsVersion(null);
      setCatalogRoot(null);
      setAddonsResolved(Boolean(enabled));
      return undefined;
    }

    let cancelled = false;
    setAddonsResolved(false);
    setAddonsVersion(null);

    const unsubscribeAddons = onValue(
      ref(db, `agencies/${agencyID}/addons`),
      (snapshot) => {
        if (cancelled) return;
        setAgencyAddons(snapshot.val() || null);
        setAddonsResolved(true);
      },
      () => {
        if (cancelled) return;
        setAgencyAddons(null);
        setAddonsResolved(true);
      }
    );

    const unsubscribeVersion = onValue(
      ref(db, `agencies/${agencyID}/${AGENCY_ADDONS_VERSION_RTD_KEY}`),
      (snapshot) => {
        if (cancelled) return;
        setAddonsVersion(normalizeAddonsVersion(snapshot.val()));
      },
      () => {
        if (cancelled) return;
        setAddonsVersion(normalizeAddonsVersion(null));
      }
    );

    return () => {
      cancelled = true;
      unsubscribeAddons();
      unsubscribeVersion();
    };
  }, [agencyID, authUser?.uid, enabled]);

  useEffect(() => {
    if (!enabled || !authUser?.uid || !agencyID || !addonsVersion) {
      setCatalogRoot(null);
      setCatalogResolved(false);
      return undefined;
    }

    let cancelled = false;
    setCatalogResolved(false);
    const unsubscribeCatalog = onValue(
      ref(db, featureAddonCatalogPath(addonsVersion)),
      (snapshot) => {
        if (cancelled) return;
        setCatalogRoot(snapshot.val() || null);
        setCatalogResolved(true);
      },
      () => {
        if (cancelled) return;
        setCatalogRoot(null);
        setCatalogResolved(true);
      }
    );

    return () => {
      cancelled = true;
      unsubscribeCatalog();
    };
  }, [addonsVersion, agencyID, authUser?.uid, enabled]);

  return {
    agencyAddons,
    catalogRoot,
    addonsResolved,
    catalogResolved,
    enabled,
  };
};

/**
 * Live `agencies/${agencyID}/addons` plus the catalog at
 * `featureAddons/{addonsVersion}`. Replaces getFeatureAddonStatus for the paywall.
 */
const useAgencyFeatureAddons = ({ addonKey, enabled }) => {
  const { agencyAddons, catalogRoot, addonsResolved } = useAgencyAddonSource({
    enabled,
  });
  const waitersRef = useRef([]);

  const status = useMemo(() => {
    if (!enabled || !addonsResolved || !addonKey) return null;
    return buildFeatureAddonStatus({
      addonKey,
      agencyAddons,
      catalogRoot,
      eligible: true,
    });
  }, [addonKey, addonsResolved, agencyAddons, catalogRoot, enabled]);

  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!addonsResolved || !status) return;
    const waiters = waitersRef.current.splice(0);
    waiters.forEach((resolve) => resolve(status));
  }, [addonsResolved, status]);

  const refreshStatus = useCallback(() => {
    if (!enabled) {
      return Promise.resolve({
        ok: true,
        eligible: false,
        ungated: true,
        effective: null,
        tiers: [],
        agencyAddon: null,
      });
    }
    if (addonsResolved && statusRef.current) {
      return Promise.resolve(statusRef.current);
    }
    return new Promise((resolve) => {
      waitersRef.current.push(resolve);
    });
  }, [addonsResolved, enabled]);

  return {
    status,
    isLoading: Boolean(enabled && !addonsResolved),
    refreshStatus,
  };
};

/** One listener for the three addon rows on Billing. */
export const useAgencyFeatureAddonList = ({ enabled }) => {
  const { agencyAddons, catalogRoot, addonsResolved } = useAgencyAddonSource({
    enabled,
  });

  const statuses = useMemo(() => {
    if (!enabled || !addonsResolved) return [];
    return TIERED_ADDON_KEYS.map((addonKey) => ({
      addonKey,
      ...buildFeatureAddonStatus({
        addonKey,
        agencyAddons,
        catalogRoot,
        eligible: true,
      }),
    }));
  }, [addonsResolved, agencyAddons, catalogRoot, enabled]);

  return {
    statuses,
    seatsAddon: agencyAddons?.seats ?? null,
    isLoading: Boolean(enabled && !addonsResolved),
  };
};

/**
 * White-label status from the same catalog listener as the other add-ons,
 * plus setup, plan, AppSumo, and theme. No getFeatureAddonStatus call.
 */
export const useWhiteLabelAddon = ({ enabled = true } = {}) => {
  const dispatch = useDispatchApp();
  const authUser = useSelectorApp((state) => state.auth.user);
  const { agencyID } = dispatch(getPathDatabaseByRole());
  const { agencyAddons, catalogRoot, addonsResolved } = useAgencyAddonSource({
    enabled,
  });
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const [setup, setSetup] = useState(null);
  const [advertiser, setAdvertiser] = useState(null);
  const [themeExists, setThemeExists] = useState(false);
  const [contextResolved, setContextResolved] = useState(false);

  useEffect(() => {
    if (!enabled || !authUser?.uid || !agencyID) {
      setSetup(null);
      setAdvertiser(null);
      setThemeExists(false);
      setContextResolved(Boolean(!enabled));
      return undefined;
    }

    let cancelled = false;
    setContextResolved(false);
    const pending = {
      setup: false,
      plan: false,
      planActive: false,
      custom: false,
      theme: false,
    };
    const mark = (key) => {
      pending[key] = true;
      if (cancelled) return;
      if (Object.values(pending).every(Boolean)) setContextResolved(true);
    };
    const planState = {
      subscriptionPlan: null,
      isPlanActive: null,
      subscriptionPlanInfo: null,
    };
    const publishAdvertiser = () => {
      if (cancelled) return;
      setAdvertiser({ ...planState });
    };

    const unsubscribeSetup = onValue(
      ref(db, `agencies/${agencyID}/whiteLabelSetup`),
      (snapshot) => {
        if (!cancelled) setSetup(snapshot.val() || null);
        mark("setup");
      },
      () => mark("setup")
    );
    const unsubscribePlan = onValue(
      ref(db, `advertisers/${agencyID}/subscriptionPlan`),
      (snapshot) => {
        planState.subscriptionPlan = snapshot.val() || null;
        publishAdvertiser();
        mark("plan");
      },
      () => mark("plan")
    );
    const unsubscribePlanActive = onValue(
      ref(db, `advertisers/${agencyID}/isPlanActive`),
      (snapshot) => {
        planState.isPlanActive = snapshot.val() === true;
        publishAdvertiser();
        mark("planActive");
      },
      () => mark("planActive")
    );
    const unsubscribeCustom = onValue(
      ref(db, `advertisers/${agencyID}/subscriptionPlanInfo/isCustom`),
      (snapshot) => {
        planState.subscriptionPlanInfo = { isCustom: snapshot.val() === true };
        publishAdvertiser();
        mark("custom");
      },
      () => mark("custom")
    );
    const unsubscribeTheme = onValue(
      ref(db, `themes/${agencyID}`),
      (snapshot) => {
        if (!cancelled) {
          setThemeExists(snapshot.exists() && agencyID !== ID_ADLE_THEME);
        }
        mark("theme");
      },
      () => mark("theme")
    );

    return () => {
      cancelled = true;
      unsubscribeSetup();
      unsubscribePlan();
      unsubscribePlanActive();
      unsubscribeCustom();
      unsubscribeTheme();
    };
  }, [agencyID, authUser?.uid, enabled]);

  const resolved = addonsResolved && contextResolved && isAppSumoResolved;
  const status = useMemo(() => {
    if (!enabled || !resolved) return null;
    const catalogStatus = buildFeatureAddonStatus({
      addonKey: FEATURE_ADDON_KEYS.WHITE_LABEL,
      agencyAddons,
      catalogRoot,
      eligible: true,
    });
    return buildWhiteLabelClientStatus({
      catalogEntry: catalogRoot?.[FEATURE_ADDON_KEYS.WHITE_LABEL] || null,
      agencyAddon: catalogStatus.agencyAddon,
      setup,
      advertiser,
      isAppSumo,
      themeExists,
      effective: catalogStatus.effective,
      tiers: catalogStatus.tiers,
    });
  }, [
    advertiser,
    agencyAddons,
    catalogRoot,
    enabled,
    isAppSumo,
    resolved,
    setup,
    themeExists,
  ]);

  const statusRef = useRef(status);
  statusRef.current = status;

  const refreshStatus = useCallback(() => {
    if (!enabled) return Promise.resolve({ ok: false });
    return Promise.resolve(statusRef.current);
  }, [enabled]);

  return {
    status,
    whiteLabel: status?.whiteLabel || null,
    isLoading: Boolean(enabled && authUser?.uid && !resolved),
    refreshStatus,
  };
};

export default useAgencyFeatureAddons;

/**
 * Seat limits are derived on the client. Plan fields come from the selected
 * advertiser, usage from `adsUsers/{agencyID}`, and price from the seats
 * catalog plus the agency addon node. Only the token's agencyID claim is used.
 */
export const useAgencySeats = ({ enabled = true } = {}) => {
  const authUser = useSelectorApp((state) => state.auth.user);
  const agencyID =
    typeof authUser?.agencyID === "string" ? authUser.agencyID.trim() : "";
  const planInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );
  const isPlanActive = useSelectorApp(
    (state) => state.payments.isPlanActive === true
  );
  const isPlanActiveResolved = useSelectorApp(
    (state) => state.payments.isPlanActiveResolved === true
  );
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const { agencyAddons, catalogRoot, addonsResolved, catalogResolved } =
    useAgencyAddonSource({ enabled });
  const [users, setUsers] = useState(null);
  const [usersResolved, setUsersResolved] = useState(false);
  const [override, setOverride] = useState(null);
  const waitersRef = useRef([]);

  useEffect(() => {
    if (!enabled || !authUser?.uid || !agencyID) {
      setUsers(null);
      setUsersResolved(false);
      return undefined;
    }

    let cancelled = false;
    const unsubscribe = onValue(
      ref(db, `adsUsers/${agencyID}`),
      (snapshot) => {
        if (cancelled) return;
        setUsers(snapshot.val() || null);
        setUsersResolved(true);
      },
      () => {
        if (cancelled) return;
        setUsers(null);
        setUsersResolved(true);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [agencyID, authUser?.uid, enabled]);

  const active = Boolean(enabled && authUser?.uid && agencyID);
  const inputsReady =
    active &&
    addonsResolved &&
    catalogResolved &&
    isAppSumoResolved &&
    isPlanActiveResolved &&
    usersResolved;

  const addonState = agencyAddons?.seats ?? null;

  const derived = useMemo(() => {
    if (!inputsReady) return null;
    const planName =
      typeof subscriptionPlan === "string" ? subscriptionPlan : null;
    const catalogEntry = catalogRoot?.seats ?? null;
    const status = buildSeatsStatus({
      catalog: catalogEntry,
      isAppSumo,
      planInfo,
      subscriptionPlan: planName,
      isPlanActive,
      users,
      addonState,
    });
    return status;
  }, [
    addonState,
    catalogRoot,
    inputsReady,
    isAppSumo,
    isPlanActive,
    planInfo,
    subscriptionPlan,
    users,
  ]);

  useEffect(() => {
    if (!override || !derived) return;
    const sameQuantity =
      override.quantity == null || override.quantity === derived.quantity;
    const sameEntitled =
      override.entitledSeats == null ||
      override.entitledSeats === derived.entitledSeats;
    if (sameQuantity && sameEntitled) setOverride(null);
  }, [derived, override]);

  const seatsStatus = useMemo(() => {
    if (!override) return derived;
    if (!derived) return override;
    return {
      ...override,
      usedSeats: derived.usedSeats,
      minQuantity: derived.minQuantity,
    };
  }, [derived, override]);

  const statusRef = useRef(seatsStatus);
  statusRef.current = seatsStatus;

  useEffect(() => {
    if (!seatsStatus) return;
    const waiters = waitersRef.current.splice(0);
    waiters.forEach((resolve) => resolve(seatsStatus));
  }, [seatsStatus]);

  const refreshSeatsStatus = useCallback(() => {
    if (!enabled || !agencyID) return Promise.resolve(null);
    if (statusRef.current) return Promise.resolve(statusRef.current);
    return new Promise((resolve) => {
      waitersRef.current.push(resolve);
    });
  }, [agencyID, enabled]);

  const applySeatsStatus = useCallback((data) => {
    if (!data || typeof data !== "object") return;
    setOverride(data);
  }, []);

  return {
    seatsStatus,
    isLoading: active && !inputsReady && !override,
    refreshSeatsStatus,
    applySeatsStatus,
  };
};
